// BiSolution Analytics — "Midnight Sapphire" Design System
// Single source of truth for all design tokens

export const palette = {
  primary: { main: '#6366F1', light: '#818CF8', dark: '#4F46E5', 50: '#EEF2FF', contrastText: '#FFFFFF' },
  secondary: { main: '#EC4899', light: '#F472B6', dark: '#DB2777', contrastText: '#FFFFFF' },
  accent: '#06B6D4',
  success: { main: '#10B981', light: '#34D399', dark: '#059669', bg: 'rgba(16, 185, 129, 0.12)' },
  warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706', bg: 'rgba(245, 158, 11, 0.12)' },
  error: { main: '#EF4444', light: '#F87171', dark: '#DC2626', bg: 'rgba(239, 68, 68, 0.12)' },
  info: { main: '#3B82F6', light: '#60A5FA', dark: '#2563EB', bg: 'rgba(59, 130, 246, 0.12)' },
};

export const dark = {
  bg: { default: '#0B0F1A', paper: '#111827', elevated: '#1E293B', glass: 'rgba(17, 24, 39, 0.72)', glassHover: 'rgba(17, 24, 39, 0.85)' },
  border: { subtle: 'rgba(148, 163, 184, 0.08)', default: 'rgba(148, 163, 184, 0.16)', focus: 'rgba(99, 102, 241, 0.5)' },
  text: { primary: '#F1F5F9', secondary: '#94A3B8', disabled: '#475569', hint: '#64748B' },
  divider: 'rgba(148, 163, 184, 0.08)',
  action: { hover: 'rgba(148, 163, 184, 0.04)', selected: 'rgba(99, 102, 241, 0.08)', focus: 'rgba(99, 102, 241, 0.12)' },
};

export const light = {
  bg: { default: '#F8FAFC', paper: '#FFFFFF', elevated: '#FFFFFF', glass: 'rgba(255, 255, 255, 0.72)', glassHover: 'rgba(255, 255, 255, 0.85)' },
  border: { subtle: 'rgba(15, 23, 42, 0.06)', default: 'rgba(15, 23, 42, 0.12)', focus: 'rgba(99, 102, 241, 0.5)' },
  text: { primary: '#0F172A', secondary: '#64748B', disabled: '#94A3B8', hint: '#94A3B8' },
  divider: 'rgba(15, 23, 42, 0.06)',
  action: { hover: 'rgba(15, 23, 42, 0.04)', selected: 'rgba(99, 102, 241, 0.06)', focus: 'rgba(99, 102, 241, 0.08)' },
};

export const gradients = {
  primary: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
  secondary: 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)',
  accent: 'linear-gradient(135deg, #06B6D4 0%, #6366F1 100%)',
  success: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
  warning: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
  hero: 'linear-gradient(135deg, #0B0F1A 0%, #1E1B4B 50%, #0B0F1A 100%)',
  sidebar: 'linear-gradient(180deg, #0F172A 0%, #0B0F1A 100%)',
  sidebarLight: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
  cardGlass: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%)',
  navIndicator: 'linear-gradient(180deg, #6366F1, #8B5CF6)',
  tabIndicator: 'linear-gradient(90deg, #6366F1, #8B5CF6)',
  progressGreen: 'linear-gradient(90deg, #10B981, #06B6D4)',
  progressYellow: 'linear-gradient(90deg, #F59E0B, #EF4444)',
  progressRed: 'linear-gradient(90deg, #EF4444, #DC2626)',
  barPrimary: 'linear-gradient(90deg, #6366F1, #8B5CF6)',
};

export const chartColors = {
  palette: ['#6366F1', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#64748B'],
  paletteDark: ['#818CF8', '#22D3EE', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#F472B6', '#2DD4BF', '#FB923C', '#94A3B8'],
};

export const shadows = {
  card: '0 1px 3px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.04)',
  cardHover: '0 8px 25px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(99, 102, 241, 0.08)',
  cardLight: '0 1px 3px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(15, 23, 42, 0.04)',
  cardLightHover: '0 8px 25px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(99, 102, 241, 0.12)',
  button: '0 4px 14px rgba(99, 102, 241, 0.35)',
  buttonHover: '0 6px 20px rgba(99, 102, 241, 0.45)',
  dialog: '0 25px 50px rgba(0, 0, 0, 0.5)',
  glow: '0 0 20px rgba(99, 102, 241, 0.3)',
  navbar: '0 1px 0 rgba(148, 163, 184, 0.08)',
};

export const radius = { xs: 6, sm: 8, md: 10, lg: 12, xl: 16, xxl: 20, full: 9999 };

export const fontFamily = {
  primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
};

export const transitions = {
  fast: '150ms ease', normal: '200ms ease', slow: '300ms ease',
  spring: '300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
};

export const blur = { sm: 'blur(8px)', md: 'blur(12px)', lg: 'blur(20px)', xl: 'blur(24px)' };
